import { supabase, getAuthUser } from '@/lib/supabase'
import { parseFacets, type Facet } from '@/types/facets'
import { isDocumentKind, type DocumentKind, type DocumentStatus } from '@/types/document'

/** Entity kinds the `attachments` table accepts (its CHECK constraint). Events
 *  attach under 'event_note', keyed by the stable Google event id — the same
 *  key event notes use — so no note row needs to exist first. 'document' is a
 *  shelf upload with no parent entity: entity_id is the user's own id. */
export type AttachmentEntityType = 'task' | 'project' | 'event_note' | 'instance_note' | 'note' | 'routine' | 'document' | 'choice_option'

export interface Attachment {
  id: string
  fileName: string
  fileType: string
  url: string
  /** Typed facts extracted by analyze-attachment; [] = analyzed, nothing found. */
  facets: Facet[]
  analyzedAt: string | null
  /** Null unless analyze-attachment classified this as a durable document. */
  documentStatus: DocumentStatus | null
  documentKind: DocumentKind | null
  documentLabel: string | null
}

/** Document types the storage bucket's allowed_mime_types accepts. Images are
 *  handled separately (converted to JPEG so HEIC/WebP pastes always land). */
const DOCUMENT_MIME_TYPES: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'audio/mpeg': 'mp3',
}

/** `accept` attribute for a picker offering everything attachFile can take. */
export const ATTACHMENT_ACCEPT = ['image/*', ...Object.keys(DOCUMENT_MIME_TYPES)].join(',')

/** Longest side of an uploaded JPEG — plenty for viewing/vision, kind to egress. */
const MAX_DIMENSION = 1600

export async function toJpeg(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode image'))),
      'image/jpeg',
      0.8,
    )
  })
}

/** Attachments on an entity (the `attachments` table + bucket), with
 *  short-lived signed URLs for display/download. */
export async function listAttachments(
  entityType: AttachmentEntityType,
  entityId: string,
): Promise<Attachment[]> {
  const { data: rows, error } = await supabase
    .from('attachments')
    .select('id, file_name, file_type, storage_path, facets, analyzed_at, document_status, document_kind, document_label')
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('created_at', { ascending: true })
  if (error || !rows) return []

  const attachments: Attachment[] = []
  for (const row of rows) {
    const { data } = await supabase.storage
      .from('attachments')
      .createSignedUrl(row.storage_path, 3600)
    if (data?.signedUrl) {
      attachments.push({
        id: row.id,
        fileName: row.file_name,
        fileType: row.file_type,
        url: data.signedUrl,
        facets: parseFacets(row.facets),
        analyzedAt: row.analyzed_at ?? null,
        documentStatus: (row.document_status as DocumentStatus | null) ?? null,
        documentKind: isDocumentKind(row.document_kind) ? row.document_kind : null,
        documentLabel: row.document_label ?? null,
      })
    }
  }
  return attachments
}

/**
 * Attach a file to an entity: upload to storage → insert an attachments row.
 * Images are downscaled/re-encoded to JPEG; documents upload as-is when the
 * bucket accepts their MIME type. Returns the new row's id + stored content
 * type (the caller decides whether to analyze), or null on failure.
 */
export async function attachFile(
  entityType: AttachmentEntityType,
  entityId: string,
  file: Blob,
  fileName?: string,
): Promise<{ id: string; contentType: string } | null> {
  try {
    const { data: { user } } = await getAuthUser()
    if (!user) throw new Error('Not signed in')

    let upload: Blob
    let contentType: string
    let extension: string
    if (file.type.startsWith('image/') || file.type === '') {
      upload = await toJpeg(file)
      contentType = 'image/jpeg'
      extension = 'jpg'
    } else if (DOCUMENT_MIME_TYPES[file.type]) {
      upload = file
      contentType = file.type
      extension = DOCUMENT_MIME_TYPES[file.type]
    } else {
      throw new Error(`Unsupported file type: ${file.type}`)
    }

    const storagePath = `${user.id}/attach/${crypto.randomUUID()}.${extension}`

    const { error: uploadErr } = await supabase.storage
      .from('attachments')
      .upload(storagePath, upload, { contentType, upsert: false })
    if (uploadErr) throw new Error(uploadErr.message)

    const { data: inserted, error: insertErr } = await supabase.from('attachments').insert({
      user_id: user.id,
      entity_type: entityType,
      entity_id: entityId,
      file_name: fileName || `attachment.${extension}`,
      file_type: contentType,
      file_size: upload.size,
      storage_path: storagePath,
    }).select('id').single()
    if (insertErr || !inserted) throw new Error(insertErr?.message ?? 'Insert returned nothing')
    return { id: inserted.id as string, contentType }
  } catch (err) {
    console.error('attachFile failed:', err)
    return null
  }
}

/** Fire the vision extraction for an attachment (image/PDF). Fire-and-forget
 *  friendly: returns false on any error; the attachment stands on its own. */
export async function analyzeAttachment(attachmentId: string, entityContext?: string): Promise<boolean> {
  try {
    const { error } = await supabase.functions.invoke('analyze-attachment', {
      body: { attachmentId, entityContext },
    })
    return !error
  } catch (err) {
    console.error('analyzeAttachment failed:', err)
    return false
  }
}

/**
 * Delete an attachment: remove the row (RLS enforces ownership), then
 * best-effort remove the stored file. Row first — a failed storage removal
 * only orphans a private blob; the reverse would leave a dead link in the UI.
 */
export async function deleteAttachment(id: string): Promise<boolean> {
  try {
    const { data: row, error: fetchErr } = await supabase
      .from('attachments')
      .select('storage_path')
      .eq('id', id)
      .single()
    if (fetchErr || !row) throw new Error(fetchErr?.message ?? 'Attachment not found')

    const { error: deleteErr } = await supabase.from('attachments').delete().eq('id', id)
    if (deleteErr) throw new Error(deleteErr.message)

    const { error: storageErr } = await supabase.storage.from('attachments').remove([row.storage_path])
    if (storageErr) console.warn('deleteAttachment: row removed but storage cleanup failed:', storageErr.message)
    return true
  } catch (err) {
    console.error('deleteAttachment failed:', err)
    return false
  }
}

/**
 * Answer a document proposal: 'kept' puts it on the Documents shelf,
 * 'dismissed' means never ask about this file again. Scope is deliberately
 * untouched — it stays at the column default ('private') until the user
 * shares it from the shelf.
 */
export async function setDocumentStatus(
  id: string,
  status: 'kept' | 'dismissed',
): Promise<boolean> {
  const { error } = await supabase
    .from('attachments')
    .update({ document_status: status })
    .eq('id', id)
  if (error) {
    console.error('setDocumentStatus failed:', error.message)
    return false
  }
  return true
}
